import React, { useEffect } from 'react';
import MultiDropdown from '@/components/MultiDropdown';
import { ADD_METRICS_BUTTON } from '@/constants/test-ids';
import { CustomizeAggregationButton } from '@/features/countIf/CustomizeAggregationButton';
import AggregateOperation, { COUNT_UNIQUE } from '@/util/AggregateOperation';
import styled from 'styled-components';
import NewWorldColumn from '@/util/NewWorldColumn';
import ColumnName from '@/util/ColumnName';
import AggregateFunction from '@/util/AggregateFunction';
import { useSelector } from 'react-redux';
import { getDatasourceDbType } from '@/store-selectors';
import { PlusButton } from '@/views/QueryBuilder/subcomponents/PlusButton';
import convertColumnTypeToDisplayName from '@/util/ColumnTypeToDisplayNameConverter';
import {
  openMetricsDropdown,
  selectMetricsDropdownOpened,
} from '@/components/layers/LayerAggregate/layerAggregateSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { isIdColumnName } from '@/util/helpers/id-helpers';
import { Label } from '@trevor/ui-components';
import DropdownButton from '../../DropdownButton';
import DropdownItem from '../../../util/DropdownItem';

export type MetricColumnsDropdownListProps = {
  columns: NewWorldColumn[];
  getTableOrInsightName: F2<string, boolean, string>;
  onSelectMetric: F<AggregateFunction>;
  showCustomizeButton: boolean;
  openModal?: F<AggregateFunction>;
};

export const MetricColumnsDropdownList: React.FC<
  MetricColumnsDropdownListProps
> = ({
  columns,
  getTableOrInsightName,
  onSelectMetric,
  showCustomizeButton,
  openModal,
}) => {
  const dbType = useSelector(getDatasourceDbType);
  const dispatch = useAppDispatch();
  const metricsDropdownOpened = useAppSelector(selectMetricsDropdownOpened);

  // TODO: Replace with React.useEffect once we migrate to vi + vitest
  useEffect(() => {
    if (metricsDropdownOpened) {
      dispatch(openMetricsDropdown(false));
    }
  }, [metricsDropdownOpened]);

  const buildMetricsMenu = () => [countDropdown(), ...standardMetrics(false)];

  // get suggested metrics
  const suggestedMetrics = () =>
    columns
      .filter((c) => c.isPk())
      .map((c) => {
        const alias = c.name().alias();
        const tableName = getTableOrInsightName(alias, false);
        const newMetric = buildAggregateFunction(COUNT_UNIQUE, c.name());
        return DropdownItem.action(
          <MenuItemWrapper>
            <span data-testid={`count-${tableName}-suggested`}>
              count <b>{tableName}</b>{' '}
              <small className="faint">suggested</small>
            </span>
            {showCustomizeButton && openModal && (
              <CustomizeAggregationButton
                aggregateFunction={newMetric}
                openModal={openModal}
              />
            )}
          </MenuItemWrapper>,
          (e: React.MouseEvent) => {
            // see CustomizeAggregationButton#openCustomizeModal
            if (!e.defaultPrevented) onSelectMetric(newMetric);
          },
        );
      });

  // build count dropdown, combining row count and suggested metrics
  const countDropdown = () => {
    const options = suggestedMetrics();
    const rowCount = buildAggregateFunction(COUNT_UNIQUE, null);
    options.push(
      DropdownItem.action(
        <MenuItemWrapper>
          <span>row count</span>
          {showCustomizeButton && openModal && (
            <CustomizeAggregationButton
              aggregateFunction={rowCount}
              openModal={openModal}
            />
          )}
        </MenuItemWrapper>,
        (e: React.MouseEvent) => {
          // see CustomizeAggregationButton#openCustomizeModal
          if (!e.defaultPrevented) onSelectMetric(rowCount);
        },
      ),
    );
    // add a divider below the suggested metrics
    options.push(DropdownItem.divider());
    const countUnique = standardMetrics(true);

    options.push(
      DropdownItem.group(
        <Label variant={1}>Count distinct</Label>,
        // @ts-expect-error: Inferring unknown should help/
        countUnique[0]?.map((cu: unknown) => cu),
      ),
    );

    return DropdownItem.dropdown(<span>count</span>, options);
  };

  // return supported columns (per metric) for each alias
  const supportedColumnsByAlias = (
    allColumns: NewWorldColumn[],
    uniqueAlias: string,
    metric: AggregateOperation,
  ) =>
    allColumns.filter((a) => {
      const alias = a.name().hasAlias() ? a.name().alias() : 'custom column';
      // Don't show columns called 'id' or containing '_id' in the sum and avg options
      const notID =
        !['sum', 'avg'].includes(metric.name()) ||
        !isIdColumnName(a.name().name());
      // check that column belongs to alias AND is supported by metric type
      return (
        notID &&
        alias === uniqueAlias &&
        metric.supportedTypes().some((r) => r === a.type().type())
      );
    });

  // this is how metrics are stored
  const buildAggregateFunction = (
    aggregateOperation: AggregateOperation,
    columnName: Nullable<ColumnName>,
  ) => new AggregateFunction(aggregateOperation, columnName);

  // take a bool to return only count unique metrics, as these are treated separately (added as the first option and contain the suggestions and also row count)
  const standardMetrics = (isCountUnique: boolean) => {
    const _metrics = AggregateOperation.all(dbType) as AggregateOperation[];
    const uniqueAliases = [
      ...new Set(
        columns.map((c) =>
          c.name().hasAlias() ? c.name().alias() : 'custom column',
        ),
      ),
    ];
    // return 'count unique' metrics or others, based on isCountUnique argument
    return _metrics
      .filter((m) => (m.name() === 'count') === isCountUnique)
      .map((m) => {
        const metricName = m.longName();
        // create a dropdown item only for metrics that have column options
        const _aliases = uniqueAliases
          .filter((uniqueAlias) => {
            // check if that alias contains any supported columns
            const supportedColumns = supportedColumnsByAlias(
              columns,
              uniqueAlias,
              m,
            );
            // now remove any columns that aren't visible
            const visibleColumns = supportedColumns.filter((c) =>
              c.isVisible(),
            );
            return visibleColumns.length > 0;
          })
          .map((ua) => {
            const tableName = getTableOrInsightName(ua, true);
            const supportedColumns = supportedColumnsByAlias(columns, ua, m);
            const visibleColumns = supportedColumns.filter((c) =>
              c.isVisible(),
            );
            const items = visibleColumns.map((c) => {
              const newMetric = buildAggregateFunction(m, c.name());
              return (
                // create a dropdown action to pass into a dropdown item
                DropdownItem.action(
                  <MenuItemWrapper>
                    <span>
                      {c.name().name()}{' '}
                      <small className="faint">
                        {convertColumnTypeToDisplayName(c.type().type())}
                      </small>
                    </span>
                    {showCustomizeButton && openModal && (
                      <CustomizeAggregationButton
                        aggregateFunction={newMetric}
                        openModal={openModal}
                      />
                    )}
                  </MenuItemWrapper>,
                  (e: React.MouseEvent) => {
                    // see CustomizeAggregationButton#openCustomizeModal
                    if (!e.defaultPrevented) onSelectMetric(newMetric);
                  },
                  undefined,
                  c.name().name(),
                )
              );
            });
            return (
              // create a subdropdown menu that we will pass into the main dropdown
              DropdownItem.dropdownWithSearch(tableName, items)
            );
          });

        // if returnCountUnique is true, just return the table dropdown containing the cols
        if (isCountUnique) {
          return _aliases;
        }

        // const arg = uniqueAliases.length === 1 ? aliases[0] : aliases;

        return DropdownItem.dropdown(metricName, _aliases);
      });
  };

  return (
    <DropdownButton
      open={metricsDropdownOpened}
      // @ts-expect-error: types mismatch. Refactor/rewrite MultiDropdown.
      contentBuilder={() => <MultiDropdown items={buildMetricsMenu()} />}
    >
      <PlusButton data-testid={ADD_METRICS_BUTTON} />
    </DropdownButton>
  );
};

const MenuItemWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 10px;

  :hover {
    button {
      visibility: visible;
    }
  }

  button {
    visibility: hidden;
  }
`;
